import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pill, Plus, SearchX } from 'lucide-react';
import {
  BUSINESS_STATUSES,
  resolveCloudPosListState,
  type BusinessStatus,
  type PharmacyQuery,
  type PharmacySortKey,
  type SortDirection,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { CountryDisplay, CountrySelect } from '@/components/location';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Button,
  DemoBadge,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  Pagination,
  SearchInput,
  SkeletonRows,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { listPharmacies, useQuery } from '@/data';
import { cn } from '@/lib/cn';
import { PharmacyActionsMenu } from './actions/PharmacyActionsMenu';
import { CloudPosBadge } from './components/CloudPosBadge';
import { BUSINESS_STATUS_LABELS } from './components/labels';
import { BusinessStatusBadge } from './components/StatusBadges';
import { AddPharmacyWizard } from './wizard/AddPharmacyWizard';

const PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
const SORT_KEYS: readonly PharmacySortKey[] = ['name', 'country', 'status', 'createdAt'];

const STATUS_OPTIONS = BUSINESS_STATUSES.map((s) => ({ value: s, label: BUSINESS_STATUS_LABELS[s] }));

const COLUMN_COUNT = 6;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function PharmaciesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const can = useCan();
  const canCreate = can('pharmacy.create');

  const q = params.get('q') ?? '';
  const country = params.get('country') ?? '';
  const statusParam = params.get('status') ?? '';
  const status = (BUSINESS_STATUSES as readonly string[]).includes(statusParam) ? (statusParam as BusinessStatus) : '';
  const sortParam = params.get('sort') as PharmacySortKey | null;
  const sortBy = sortParam && SORT_KEYS.includes(sortParam) ? sortParam : 'name';
  const sortDir: SortDirection = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const sizeParam = Number(params.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(sizeParam) ? sizeParam : DEFAULT_PAGE_SIZE;

  const [searchInput, setSearchInput] = useState(q);
  const debouncedSearch = useDebounced(searchInput, 250);

  const update = (patch: Record<string, string | number | null>, resetPage = true) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, String(value));
        }
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (debouncedSearch !== q) update({ q: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (q !== debouncedSearch && q !== searchInput) setSearchInput(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const query: PharmacyQuery = useMemo(
    () => ({ search: q, country: country || undefined, status: status || undefined, sort: sortBy, order: sortDir, page, pageSize }),
    [q, country, status, sortBy, sortDir, page, pageSize],
  );

  const { data, error, loading, refreshing, reload } = useQuery(() => listPharmacies(query), [query]);

  const filtersActive = !!(q.trim() || country || status);
  const clearFilters = () => {
    setSearchInput('');
    update({ q: null, country: null, status: null });
  };

  const sortProps = (key: PharmacySortKey) => ({
    sortable: true,
    sortDirection: sortBy === key ? sortDir : null,
    onSort: () => update({ sort: key, dir: sortBy === key && sortDir === 'asc' ? 'desc' : 'asc' }, false),
  });

  return (
    <>
      <PageHeader
        title="Pharmacies"
        subtitle="Manage all pharmacy customers and installations"
        actions={
          canCreate && (
            <Button leftIcon={<Plus size={16} aria-hidden="true" />} onClick={() => setWizardOpen(true)}>
              Add Pharmacy
            </Button>
          )
        }
      />

      <div className="card">
        <FilterBar dirty={filtersActive} onReset={clearFilters}>
          <SearchInput
            label="Search pharmacies"
            placeholder="Search pharmacy..."
            value={searchInput}
            onChange={setSearchInput}
            className="w-full lg:max-w-xs"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:items-center">
            <div className="lg:w-56">
              <CountrySelect value={country} onChange={(v) => update({ country: v })} allowEmpty emptyLabel="All countries" />
            </div>
            <FilterSelect label="Status" allLabel="All" value={status} onChange={(v) => update({ status: v })} options={STATUS_OPTIONS} className="lg:w-52" />
          </div>
        </FilterBar>

        {error ? (
          <ErrorState message={error.message} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
        ) : (
          <>
            <TableContainer label="Pharmacies table">
              <Table className="min-w-[820px]" aria-busy={loading || refreshing} aria-describedby="pharmacies-caption">
                <caption id="pharmacies-caption" className="sr-only">
                  Pharmacies, sorted by {sortBy} {sortDir === 'asc' ? 'ascending' : 'descending'}
                </caption>
                <THead>
                  <tr>
                    <TH {...sortProps('name')} className="pl-5">
                      Name
                    </TH>
                    <TH {...sortProps('country')}>Location</TH>
                    <TH>Branches</TH>
                    <TH {...sortProps('status')}>Status</TH>
                    <TH>Cloud POS</TH>
                    <TH align="right" className="pr-5">
                      Actions
                    </TH>
                  </tr>
                </THead>
                <TBody className={cn('transition-opacity', refreshing && 'opacity-60')}>
                  {loading ? (
                    <SkeletonRows rows={8} columns={COLUMN_COUNT} />
                  ) : data && data.items.length > 0 ? (
                    data.items.map((p) => {
                      // Defensive: stale API processes may omit cloudPos; never blank the grid.
                      const cloudPos = resolveCloudPosListState(p.cloudPos, p.branchCount);
                      return (
                      <TR
                        key={p.id}
                        interactive
                        onClick={() => navigate(`/pharmacies/${p.id}`)}
                        className="cursor-pointer"
                      >
                        <TD className="max-w-[280px] pl-5">
                          <Link
                            to={`/pharmacies/${p.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block truncate font-medium text-ink hover:text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
                          >
                            {p.name}
                          </Link>
                          <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                            {p.code}
                            {p.isDemo && <DemoBadge />}
                          </span>
                        </TD>
                        <TD>
                          <CountryDisplay country={p.country} city={p.city} />
                        </TD>
                        <TD>
                          <span className="tabular-nums text-sm text-ink">{p.branchCount}</span>
                          <span className="ml-1 text-xs text-ink-muted">
                            {p.branchCount === 1 ? 'location' : 'locations'}
                          </span>
                          {cloudPos.cloudBranchCount > 0 && cloudPos.cloudBranchCount !== p.branchCount && (
                            <span className="mt-0.5 block text-[11px] text-ink-muted">
                              {cloudPos.cloudBranchCount} cloud connected
                            </span>
                          )}
                        </TD>
                        <TD>
                          <BusinessStatusBadge status={p.status} />
                        </TD>
                        <TD>
                          <CloudPosBadge status={cloudPos.status} />
                        </TD>
                        <TD align="right" className="pr-5" onClick={(e) => e.stopPropagation()}>
                          <PharmacyActionsMenu pharmacy={{ id: p.id, name: p.name }} />
                        </TD>
                      </TR>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={COLUMN_COUNT}>
                        {filtersActive ? (
                          <EmptyState
                            icon={<SearchX size={20} aria-hidden="true" />}
                            title="No pharmacies match your filters"
                            description="Try a different search term or reset the filters to see all pharmacies."
                            action={
                              <Button variant="secondary" onClick={clearFilters}>
                                Reset
                              </Button>
                            }
                          />
                        ) : (
                          <EmptyState
                            icon={<Pill size={20} aria-hidden="true" />}
                            title="No pharmacies yet"
                            description="Add the first pharmacy customer to start managing it in Central Admin."
                            action={
                              canCreate && (
                                <Button leftIcon={<Plus size={16} aria-hidden="true" />} onClick={() => setWizardOpen(true)}>
                                  Add Pharmacy
                                </Button>
                              )
                            }
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </TBody>
              </Table>
            </TableContainer>
            {data && data.total > 0 && (
              <div className="border-t border-line">
                <Pagination
                  page={data.page}
                  pageSize={pageSize}
                  total={data.total}
                  onPageChange={(p) => update({ page: p }, false)}
                  onPageSizeChange={(s) => update({ size: s === DEFAULT_PAGE_SIZE ? null : s })}
                  pageSizeOptions={PAGE_SIZES}
                  itemLabel="pharmacies"
                />
              </div>
            )}
          </>
        )}
      </div>

      {wizardOpen && <AddPharmacyWizard onClose={() => setWizardOpen(false)} />}
    </>
  );
}
